// ============================================================================
// Módulo CORTESÍAS — todo lo regalado, trackeado (jul-2026).
//
// Cada cortesía nace en la mesa (toggle 🎁 en la selección de productos):
// el cliente la ve en $0, sale del inventario normal y acá queda el registro
// completo: fecha/hora, quién la otorgó, a nombre de quién (ago-2026), item,
// valor a precio de carta, número de pedido y número de factura si ya se
// facturó.
//
// Filtros (ago-2026): rango de fechas custom + persona (quien la registró o
// a nombre de quién quedó).
// ============================================================================
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Gift } from 'lucide-react';
import { useBranch } from '../context/branch-context';
import { listCortesias, type CortesiasResponse } from '../../api/cortesias';
import { listUsuarioNombres, type UsuarioNombre } from '../../api/usuarios';
import { ModuleHeader } from './ui/module-header';
import { formatMoney } from '@/lib/moneda';
import { useTextos } from '../context/regional-context';
import { textosCortesias } from '../../i18n/modulos/cortesias';

function inicioMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtFechaHora(s: string): string {
  return new Date(s).toLocaleString('es-CO', { timeZone: 'America/Bogota', 
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

const filtroInputStyle: CSSProperties = {
  padding: '9px 13px',
  background: 'var(--surface)',
  border: '1.5px solid var(--line-2)',
  borderRadius: 'var(--r-pill)',
  fontSize: 13.5,
  fontWeight: 700,
  color: 'var(--ink)',
  fontFamily: 'inherit',
  boxShadow: 'var(--sh-sm)',
};

export function Cortesias() {
  const { currentBranch, currentBranchId } = useBranch();
  const tx = useTextos(textosCortesias);
  // Período: atajos + rango custom (ago-2026).
  const [periodo, setPeriodo] = useState<'mes' | 'todos' | 'rango'>('mes');
  const [desde, setDesde] = useState<string>(inicioMes());
  const [hasta, setHasta] = useState<string>(hoyISO());
  // Filtro por persona: matchea "otorgada por" O "a nombre de".
  const [usuarioId, setUsuarioId] = useState<number | null>(null);
  const [usuarios, setUsuarios] = useState<UsuarioNombre[]>([]);
  const [data, setData] = useState<CortesiasResponse | null>(null);
  const [cargando, setCargando] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    listUsuarioNombres().then(setUsuarios).catch(() => { /* select queda solo con "Todos" */ });
  }, []);

  useEffect(() => {
    if (!currentBranchId) return;
    setCargando(true);
    setLoadError(null);
    listCortesias({
      sede_id: currentBranchId,
      desde: periodo === 'mes' ? inicioMes() : periodo === 'rango' ? (desde || undefined) : undefined,
      hasta: periodo === 'rango' ? (hasta || undefined) : undefined,
      usuario_id: usuarioId ?? undefined,
      limit: 500,
    })
      .then(setData)
      .catch((err: any) => {
        const status = err?.status ? ` (HTTP ${err.status})` : '';
        setLoadError(
          `${err?.message || tx('errorCargando')}${status}.` +
          (err?.status === 404 ? tx('reiniciasteApi') : '')
        );
      })
      .finally(() => setCargando(false));
  }, [currentBranchId, periodo, desde, hasta, usuarioId]);

  // ¿Alguna fila tiene "a nombre de"? Si ninguna, la columna no aporta y se oculta.
  const hayANombreDe = useMemo(
    () => (data?.items ?? []).some(c => c.a_nombre_de),
    [data]
  );

  return (
    <div className="pb-32 max-w-screen-2xl mx-auto">
      <ModuleHeader
        title={tx('titulo')}
        icon={Gift}
        stats={[
          { n: data?.resumen.cantidad ?? 0, label: tx('statCortesias'), tone: 'blue' },
          { n: `${formatMoney((data?.resumen.valor ?? 0))}`, label: tx('valorRegalado'), tone: 'warn' },
        ]}
        secondaryFilters={{
          value: periodo,
          onChange: (v) => setPeriodo(v as 'mes' | 'todos' | 'rango'),
          options: [
            { value: 'mes', label: tx('mesEnCurso') },
            { value: 'todos', label: tx('todoHistorico') },
            { value: 'rango', label: tx('rangoFechas') },
          ],
          ariaLabel: tx('periodoAria'),
        }}
      />

      <div className="px-4 sm:px-6 lg:px-8 space-y-4">
        <p style={{ color: 'var(--muted-ink)', fontSize: 14, fontWeight: 600 }}>
          {tx('itemsRegalados')} <b style={{ color: 'var(--ink)' }}>{currentBranch}</b>
        </p>

        {/* Filtros: rango custom + persona */}
        <div className="flex items-center gap-2.5 flex-wrap">
          {periodo === 'rango' && (
            <>
              <label className="inline-flex items-center gap-1.5" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted-ink)' }}>
                {tx('desde')}
                <input
                  type="date"
                  value={desde}
                  max={hasta || undefined}
                  onChange={(e) => setDesde(e.target.value)}
                  className="tabular-nums focus:outline-none"
                  style={filtroInputStyle}
                />
              </label>
              <label className="inline-flex items-center gap-1.5" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted-ink)' }}>
                {tx('hasta')}
                <input
                  type="date"
                  value={hasta}
                  min={desde || undefined}
                  onChange={(e) => setHasta(e.target.value)}
                  className="tabular-nums focus:outline-none"
                  style={filtroInputStyle}
                />
              </label>
            </>
          )}
          <label className="inline-flex items-center gap-1.5" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted-ink)' }}>
            {tx('persona')}
            <select
              value={usuarioId ?? ''}
              onChange={(e) => setUsuarioId(e.target.value === '' ? null : Number(e.target.value))}
              className="focus:outline-none"
              style={{ ...filtroInputStyle, cursor: 'pointer', maxWidth: 220 }}
              title={tx('personaTitle')}
            >
              <option value="">{tx('todasLasPersonas')}</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </label>
        </div>

        {loadError && (
          <div style={{
            padding: '12px 16px', borderRadius: 'var(--r-md)',
            background: 'rgba(220,38,38,.07)', border: '1.5px solid rgba(220,38,38,.35)',
            color: '#b91c1c', fontSize: 13.5, fontWeight: 700,
          }}>
            ⚠️ {loadError}
          </div>
        )}

        {cargando ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--muted-ink)', fontWeight: 600 }}>
            {tx('cargando')}
          </div>
        ) : (data?.items.length ?? 0) === 0 && !loadError ? (
          <div style={{
            textAlign: 'center', padding: '48px 20px', border: '1.5px dashed var(--line-2)',
            borderRadius: 'var(--r-md)', color: 'var(--muted-ink)', fontWeight: 600, fontSize: 14.5,
          }}>
            {tx('sinCortesias')} <b>{tx('botonCortesia')}</b> {tx('juntoCategorias')}
          </div>
        ) : (
          <div className="tablecard">
            <div className="tablescroll">
              <table className="cmp">
                <thead>
                  <tr>
                    <th>{tx('colFechaHora')}</th>
                    <th>{tx('colItem')}</th>
                    <th className="r">{tx('colCant')}</th>
                    <th className="r">{tx('colValor')}</th>
                    <th>{tx('colOtorgadaPor')}</th>
                    {hayANombreDe && <th>{tx('colANombreDe')}</th>}
                    <th>{tx('colPedido')}</th>
                    <th>{tx('colFactura')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map(c => (
                    <tr key={c.id}>
                      <td><span className="datecell">{fmtFechaHora(c.cortesia_at)}</span></td>
                      <td><b>🎁 {c.nombre}</b></td>
                      <td className="r tabular-nums">{c.cantidad}</td>
                      <td className="r tabular-nums"><b>{formatMoney(c.valor_cortesia)}</b></td>
                      <td>{c.usuario || '—'}</td>
                      {hayANombreDe && (
                        <td>
                          {c.a_nombre_de
                            ? <b>{c.a_nombre_de}</b>
                            : <span style={{ color: 'var(--muted-2, #94a3b8)', fontWeight: 600 }}>—</span>}
                        </td>
                      )}
                      <td><span className="font-mono">{c.referencia}</span></td>
                      <td>
                        {c.factura
                          ? <span className="font-mono">{c.factura}</span>
                          : <span style={{ color: 'var(--muted-2, #94a3b8)', fontWeight: 600 }}>{tx('sinFacturar')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tfoot">
              <span className="lbl">{tx('totalRegalado', { n: data!.resumen.cantidad })}</span>
              <span className="val">{formatMoney(data!.resumen.valor)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
